import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { GetProjectFilesResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { fetchProjectFilesFromSandbox } from "@/lib/get-project-files";
import { getConsoleAuthToken } from "@/hooks/use-console-auth-token";
import { imagineStore } from "@/stores/imagine.store";

type UseProjectFilesQueryParams = {
  projectId: string | null;
  isActive: boolean;
  sandboxInstrumented: boolean;
};

type UseProjectFilesQueryResult = ReturnType<
  typeof useQuery<GetProjectFilesResponseBody>
> & {
  invalidateProjectFiles: (targetProjectId: string | null) => Promise<void>;
  prefetchProjectFiles: (targetProjectId: string | null) => Promise<void>;
};

export function useProjectFilesQuery({
  projectId,
  isActive,
  sandboxInstrumented,
}: UseProjectFilesQueryParams): UseProjectFilesQueryResult {
  const queryClient = useQueryClient();
  const onFinish$ = imagineStore.use.chat.onFinish$();
  const onData$ = imagineStore.use.chat.onData$();

  const fetchProjectFiles = useCallback(async (targetProjectId: string) => {
    return fetchProjectFilesFromSandbox({
      projectId: targetProjectId,
      token: await getConsoleAuthToken(),
    });
  }, []);

  const invalidateProjectFiles = useCallback(
    async (targetProjectId: string | null) => {
      if (!targetProjectId) return;
      await queryClient.invalidateQueries({
        queryKey: ["project-files", targetProjectId],
      });
    },
    [queryClient],
  );

  const prefetchProjectFiles = useCallback(
    async (targetProjectId: string | null) => {
      if (!targetProjectId) return;
      await queryClient.prefetchQuery({
        queryKey: ["project-files", targetProjectId],
        queryFn: () => fetchProjectFiles(targetProjectId),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
      });
    },
    [fetchProjectFiles, queryClient],
  );

  const queryFn = useCallback(async () => {
    if (!projectId) {
      throw new Error("Missing project context");
    }
    return fetchProjectFiles(projectId);
  }, [fetchProjectFiles, projectId]);

  const query = useQuery<GetProjectFilesResponseBody>({
    queryKey: ["project-files", projectId],
    enabled: Boolean(projectId && isActive && sandboxInstrumented),
    queryFn,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!projectId || !sandboxInstrumented) {
      return;
    }
  }, [projectId, sandboxInstrumented]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const subscription = onFinish$.subscribe(() => {
      if (!sandboxInstrumented || !isActive) {
        return;
      }
      void invalidateProjectFiles(projectId);
    });

    return () => subscription.unsubscribe();
  }, [
    onFinish$,
    projectId,
    sandboxInstrumented,
    isActive,
    invalidateProjectFiles,
  ]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    let timeoutId: number | undefined;
    const subscription = onData$.subscribe((part) => {
      const isCheckpoint =
        typeof part === "object" &&
        part !== null &&
        part["type"] === "data-checkpoint";
      const isCommitDone =
        typeof part === "object" &&
        part !== null &&
        part["type"] === "data-committing-changes" &&
        typeof part["data"] === "object" &&
        part["data"] !== null &&
        (part["data"] as { state?: string }).state === "done";

      if (!isCheckpoint && !isCommitDone) {
        return;
      }

      void invalidateProjectFiles(projectId);
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        void prefetchProjectFiles(projectId);
      }, 400);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [onData$, projectId, invalidateProjectFiles, prefetchProjectFiles]);

  return {
    ...query,
    invalidateProjectFiles,
    prefetchProjectFiles,
  };
}
